/**
 * AuthGate -- composition wrapper for Convex auth boundary.
 *
 * Wraps Convex's Authenticated, Unauthenticated, and AuthLoading branches
 * into a single component that renders:
 *   - {children}           when the current path is in bypassPaths (no auth required)
 *   - <FullScreenSpinner /> while auth state is loading
 *   - {children}           when authenticated
 *   - <SignIn />           when unauthenticated
 *
 * Per specs/integration-plan.md Section 6.1 (FR-013) and FR-005 (hardened
 * bypass-path matching with exact string equality + path normalisation).
 *
 * The bypassPaths prop accepts an array of canonical absolute paths
 * (e.g., ['/public', '/signin', '/auth/callback']). Matching uses exact
 * string equality after normalisation -- never startsWith or prefix matching.
 *
 * Path normalisation (normaliseHashPath):
 *   - Strip leading "#"
 *   - Collapse double slashes
 *   - Strip trailing slash (except root "/")
 *   - Reject path-traversal sequences ("..") by leaving them intact
 *     so they never match a canonical bypass path
 */
import React from "react";
import { Authenticated, Unauthenticated, AuthLoading } from "convex/react";
import FullScreenSpinner from "../primitives/FullScreenSpinner";
import SignIn from "../../screens/auth/SignIn";
import { useHashLocation } from "../../lib/router/useHashLocation";

/* ------------------------------------------------------------------ */
/*  Path normalisation helpers (module-private)                        */
/* ------------------------------------------------------------------ */

/**
 * Normalise a hash path for bypass comparison:
 *   1. Strip leading "#"
 *   2. Percent-decode the path (to catch %2e%2e traversal)
 *   3. Collapse consecutive slashes into a single slash
 *   4. Strip trailing slash (unless the result would be empty)
 *
 * IMPORTANT: We do NOT resolve ".." segments. This is intentional --
 * paths containing ".." will never match a canonical bypass path
 * (e.g., "/public/../../admin" normalises to "/public/../../admin",
 * which does not equal "/public").
 */
function normaliseHashPath(input: string): string {
  let p = input;

  // Strip leading "#"
  if (p.startsWith("#")) {
    p = p.slice(1);
  }

  // Percent-decode to catch encoded traversal (%2e = ".")
  try {
    p = decodeURIComponent(p);
  } catch {
    // If decoding fails (malformed %), use as-is
  }

  // Collapse consecutive slashes: "//public" -> "/public"
  p = p.replace(/\/{2,}/g, "/");

  // Strip trailing slash (but keep "/" itself)
  if (p.length > 1 && p.endsWith("/")) {
    p = p.slice(0, -1);
  }

  // Ensure leading slash
  if (!p.startsWith("/")) {
    p = "/" + p;
  }

  return p;
}

/**
 * Check whether the normalised input path matches any bypass path
 * using exact string equality. No prefix matching, no startsWith.
 */
function isBypassPath(input: string, bypass: readonly string[]): boolean {
  if (bypass.length === 0) return false;
  const normalised = normaliseHashPath(input);
  return bypass.some((bp) => normalised === bp);
}

/* ------------------------------------------------------------------ */
/*  AuthGate component                                                 */
/* ------------------------------------------------------------------ */

interface AuthGateProps {
  children: React.ReactNode;
  /**
   * Array of canonical absolute paths that bypass authentication.
   * Matching uses exact string equality after path normalisation.
   * Example: ['/public', '/signin', '/auth/callback']
   */
  bypassPaths?: string[];
}

export function AuthGate({ children, bypassPaths }: AuthGateProps) {
  const [currentPath] = useHashLocation();

  // If the current path matches a bypass path, render children directly
  // without any auth check.
  if (bypassPaths && bypassPaths.length > 0 && isBypassPath(currentPath, bypassPaths)) {
    return <>{children}</>;
  }

  return (
    <>
      <AuthLoading>
        <FullScreenSpinner />
      </AuthLoading>
      <Authenticated>{children}</Authenticated>
      <Unauthenticated>
        <SignIn />
      </Unauthenticated>
    </>
  );
}

export default AuthGate;
